import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Recycle,
  Sparkles,
  Calendar,
  Layers,
  Tag,
  Lock,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Service de livraison (3 engagements) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Service de livraison
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Service de livraison de cartons {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est précieux et que la circulation sur les
            axes {city.aCity} exige de la ponctualité, notre service de
            livraison s&apos;engage contractuellement.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Engagement de ponctualité absolue",
              body: "Nous convenons d'un créneau fixe et strict. Pas d'attente interminable, nos chauffeurs se présentent à l'heure exacte validée pour ne pas ralentir vos préparatifs.",
              Icon: Calendar,
            },
            {
              title: "Garantie d'intégrité du matériel",
              body: "L'intégralité de votre commande est transportée à plat sous bâche étanche et film de protection. Vos cartons arrivent propres, secs et dotés de 100 % de leur capacité de résistance d'origine.",
              Icon: ShieldCheck,
            },
            {
              title: "Service porte-à-porte intégral",
              body: "Nous ne laissons rien sur le trottoir. Que vous résidiez dans un pavillon ou au dernier étage d'une copropriété ancienne sans ascenseur, nos équipes déposent vos fournitures directement à l'intérieur de votre pièce de stockage.",
              Icon: Truck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Reconditionné -40 % ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Éco-responsable
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              L&apos;opportunité éco-responsable : nos cartons reconditionnés
              à -40 %
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour concilier gestion de votre budget et respect de
              l&apos;environnement {city.aCity}, découvrez notre filière
              exclusive de seconde main certifiée.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Une seule utilisation au compteur",
                  body: "Issus de transferts administratifs propres ou de salons professionnels, ces cartons n'ont servi qu'une fois et offrent une robustesse équivalente au neuf.",
                  Icon: Lock,
                },
                {
                  title: "Contrôle structurel strict",
                  body: "Chaque boîte est inspectée individuellement à la main. Nous écartons tout exemplaire souple ou marqué pour ne garder que la perfection géométrique.",
                  Icon: ShieldCheck,
                },
                {
                  title: "Remise immédiate de 40 %",
                  body: "Vous profitez d'un matériel de qualité industrielle à un prix défiant toute concurrence. Un choix intelligent pour la planète et votre portefeuille.",
                  Icon: Tag,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-4 md:p-5"
                >
                  <span className="flex items-center justify-center shrink-0 h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-reconditionnes"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-viroflay-cta-reconditionne"
            >
              <Tag className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons reconditionnés -40 %
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Recycle className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="text-[40px] font-extrabold text-[#237cb5] leading-none">
                -40 %
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Cartons tracés, contrôlés un par un et remis en circuit
              propre. Une seconde vie utile, économique et écologique pour
              vos déménagements {city.aCity}.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Une seule utilisation antérieure
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Contrôle à la main, exemplaire par exemplaire
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Robustesse équivalente au neuf
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Matériel de protection professionnel ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Matériel de protection
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Matériel de protection professionnel : le bouclier antichoc
            indispensable
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour sécuriser vos biens les plus délicats et éviter les rayures
            ou bris durant les virages et freinages du camion, notre gamme
            intègre des solutions techniques majeures.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Film à bulles haute densité",
              body: "Indispensable pour envelopper vos miroirs, tableaux, écrans et objets d'art, il absorbe l'intégralité des ondes de choc.",
              Icon: Layers,
            },
            {
              title: "Cartons à croisillons spécifiques",
              body: "Le secret pour la vaisselle fine et les verres. Des alvéoles rigides isolent chaque pièce individuellement, éliminant tout risque de frottement dévastateur.",
              Icon: Package,
            },
            {
              title: "Housses de protection épaisses",
              body: "Conçues spécifiquement pour vos matelas et canapés afin de faire écran contre la poussière et l'humidité durant le transport.",
              Icon: Sparkles,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-viroflay-cta-emballage"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les accessoires de protection
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Conseils de pro ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Conseils de pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment préparer ses cartons efficacement ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour optimiser le chargement de votre camion de déménagement et
            travailler comme un professionnel {city.aCity}, suivez ces règles
            d&apos;or.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Gérez la répartition des masses",
              body: "Placez toujours les objets lourds et denses (livres, dossiers, fonds de plats) au fond du carton, et terminez par les éléments légers au sommet.",
            },
            {
              title: "Chassez le vide intérieur",
              body: "Un carton contenant des espaces vides risque de s'affaisser sous le poids des autres boîtes. Comblez les interstices avec du papier kraft ou votre linge de maison.",
            },
            {
              title: "Ne surchargez pas les boîtes",
              body: "Limitez le poids à 20 ou 25 kg maximum par carton pour faciliter la manutention et protéger la santé de votre dos.",
            },
            {
              title: "Identifiez clairement le contenu",
              body: "Utilisez un marqueur pour inscrire sur les côtés du carton la pièce de destination (Cuisine, Chambre 1, Salon) et la mention « Fragile » si nécessaire, simplifiant ainsi grandement l'aménagement à l'arrivée.",
            },
          ].map(({ title, body }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <AlertTriangle className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-white border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#EEF3F7] border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-viroflay-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
